<script setup lang="ts">
import CartSummary from "./CartSummary.vue";
import { prepareItemsForUpdate } from "~/utils/prepareItemsForCart";
import { USER_ORDER_PAYMENT_STATUS } from "~/utils/premium/constants";
import type { Cart } from "~/stores/orders";

const ordersStore = useOrdersStore();
const premiumStore = usePremiumStore();
const authStore = useAuthStore();
const { track } = useTracking();
const { pollRefundBatchConfirmation, cancelRefundBatchPolling } = useRefundBatchPolling();
const { pollPayLedgerStatus } = usePayLedgerPolling();

// Injected by the parent premium-store page so the cart can fire the
// "checkout complete" callback without an event bus (Nuxt 4 doesn't have
// the Nuxt 2 $eventBus that source uses for the same thing).
const premiumCheckoutComplete = inject<(() => void) | null>("premiumCheckoutComplete", null);

interface Props {
  disableInputs?: boolean;
  // Group-order menu customisation: true while the cart has a line the frozen menu no
  // longer allows. Blocks checkout in the UI (the backend gate is the final guard).
  orderabilityBlocked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disableInputs: false,
  orderabilityBlocked: false,
});

// ── State machine ────────────────────────────────────────────────────────
// Mirrors the source state machine 1:1 — see ShoppingCart/Index.vue from
// the Nuxt 2 ticket-503 branch. Step values are control state ("what is
// the user doing") that combines with the backend-derived
// userOrderPaymentStatus to produce checkoutView (what to render).
const CHECKOUT_STEPS = Object.freeze({
  IDLE: "idle",
  CREATING_INTENT: "creating_intent",
  PAYING: "paying",
  CONFIRMING: "confirming",
  COMPLETE: "complete",
  // A checkout attempt threw. Explicit so checkoutView renders the error view
  // regardless of whether the store populated cartError/checkoutError — see
  // the startCheckout catch. Reset to IDLE after the error beat.
  ERROR: "error",
  // Refund batch is settling but the polling budget ran out — show the
  // passive "your refund is still being confirmed" view instead of
  // sitting on the spinner indefinitely.
  INCOMPLETE_REFUND: "incomplete_refund",
} as const);

interface TerminalError {
  kind: "partial_failure" | "batch_failed";
}

interface CheckoutInfo {
  title: string;
  body: string;
  action: { text: string; handler: () => void };
}

const checkoutStep = ref<string>(CHECKOUT_STEPS.IDLE);
const checkoutType = ref<"order" | "payment">("order");
const checkoutCancelled = ref(false);
const editRedirectTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const checkoutInfo = ref<CheckoutInfo>({
  title: "",
  body: "",
  action: { text: "", handler: () => {} },
});
const paymentIntent = ref<Record<string, unknown> | null>(null);
const terminalError = ref<TerminalError | null>(null);

// Errors are shown for a beat then auto-cleared. Track the pending timer so
// overlapping retries don't race: a stale timer must not wipe a newer failure
// message. We cancel any pending clear before scheduling a new one and, when
// it fires, only clear if the message it was scheduled for is still showing.
let cartErrorClearTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleCartErrorClear(afterClear?: () => void) {
  if (cartErrorClearTimer) clearTimeout(cartErrorClearTimer);
  const snapshot = ordersStore.cartError;
  cartErrorClearTimer = setTimeout(() => {
    cartErrorClearTimer = null;
    if (ordersStore.cartError === snapshot) ordersStore.setCartError(false);
    afterClear?.();
  }, 4000);
}

// ── Derived flags ────────────────────────────────────────────────────────
const isPremium = computed(() => ordersStore.cartType === "premium");
const isEditMode = computed(() => isPremium.value || ordersStore.cartMode === "edit");
const isGroupOrder = computed(
  () => isPremium.value && !!premiumStore.selectedPremiumOrderConfig?.isGroupOrder,
);
const activeOrderId = computed(() => {
  if (isPremium.value) return premiumStore.selectedPremiumOrderConfig?.orderId ?? null;
  const chosen = ordersStore.chosenOrder;
  return chosen ? (chosen as { id?: string | number }).id ?? null : null;
});

// ── checkoutView ─────────────────────────────────────────────────────────
// The order of these branches matters — earlier branches win. Match the
// source ordering exactly so resume / refund / payment states resolve to
// the same view as the Nuxt 2 site.
const checkoutView = computed(() => {
  // Default cart for non-premium, non-edit flows.
  if (!isPremium.value && !isEditMode.value) return "cart";

  if (terminalError.value) return "terminal";

  // An in-flight refund batch (either started in this session or polled
  // back as REFUND_PENDING from the active-transaction endpoint) blocks
  // every cart edit until it resolves.
  if (
    checkoutStep.value === CHECKOUT_STEPS.INCOMPLETE_REFUND ||
    premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.REFUND_PENDING
  )
    return "refund_pending";

  // Resume on cold load: customer landed here while an older payment is
  // still settling. checkoutStep is IDLE because they haven't clicked
  // Pay in this session — render the passive "finalising" copy rather
  // than dropping straight into the StripePayment wrapper's processing
  // spinner. The mounted hook polls quietly in the background.
  if (
    checkoutStep.value === CHECKOUT_STEPS.IDLE &&
    premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.PENDING
  )
    return "finalising";

  // A checkout attempt threw. Wins over the processing branch so the error is
  // shown instead of an eternal spinner, even when no message was set.
  if (checkoutStep.value === CHECKOUT_STEPS.ERROR) return "error";

  if (
    checkoutStep.value === CHECKOUT_STEPS.CREATING_INTENT ||
    checkoutStep.value === CHECKOUT_STEPS.CONFIRMING
  )
    return "processing";

  // PAYING but no PI yet — initPremiumCheckout / modifyOrder is still
  // resolving. Show the spinner rather than a blank cart.
  if (checkoutStep.value === CHECKOUT_STEPS.PAYING && !paymentIntent.value) return "processing";

  if (ordersStore.checkoutError || ordersStore.cartError) return "error";

  if (checkoutInfo.value.title) return "info";

  if (
    checkoutStep.value === CHECKOUT_STEPS.COMPLETE ||
    premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.COMPLETE
  )
    return "success";

  return "cart";
});

// Backend rejections (e.g. the one-maker-per-day cap on a group-select day)
// return a customer-facing message; the store stashes it on cartError. Show it
// verbatim rather than the generic fallback so the customer understands why the
// order was blocked.
const checkoutErrorCopy = computed(() =>
  typeof ordersStore.cartError === "string" && ordersStore.cartError
    ? ordersStore.cartError
    : `There was a problem processing your ${checkoutType.value}. Please try again.`,
);

const terminalCopy = computed(() => {
  if (!terminalError.value) return { title: "", body: "" };
  if (terminalError.value.kind === "partial_failure") {
    return {
      title: "Your changes were processed partially",
      body: "Some refunds could not be completed. Our team has been notified and will reconcile this with you. Please don't retry. Contact support if you haven't heard from us within 24 hours.",
    };
  }
  const ref = (ordersStore.chosenOrder as { reference?: string } | false)
    ? (ordersStore.chosenOrder as { reference?: string }).reference
    : undefined;
  return {
    title: "We couldn't process your changes",
    body: `Your ${checkoutType.value} couldn't be processed. Please contact support${
      ref ? ` with reference ${ref}` : ""
    }.`,
  };
});

// canRenderStripe answers: should the StripePaymentWrapper be visible?
// True whenever there's a pay ledger that needs the customer's input —
// active checkout (PAYING with a PI), failed retry, pending after
// confirmation has started in this session, or AWAITING_PAYMENT_DETAILS
// (resumed PI from the backend, no checkoutStep yet).
const canRenderStripe = computed(() => {
  if (
    checkoutStep.value === CHECKOUT_STEPS.PAYING &&
    !!(paymentIntent.value as { client_secret?: string } | null)?.client_secret
  )
    return true;
  if (premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.FAILED) return true;
  if (
    premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.PENDING &&
    checkoutStep.value !== CHECKOUT_STEPS.CONFIRMING
  )
    return true;
  if (premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.AWAITING_PAYMENT_DETAILS)
    return true;
  return false;
});

// initialView pre-seeds the wrapper's state machine on resume so it
// doesn't probe Stripe again — we already know what state we're in.
const initialPaymentView = computed<"idle" | "confirming" | "incomplete" | "failure" | null>(
  () => {
    if (checkoutStep.value === CHECKOUT_STEPS.PAYING) return null;
    if (premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.FAILED) return "failure";
    // Resume (PENDING) is intercepted by checkoutView === "finalising"
    // before the wrapper renders, so no initial view is needed here.
    return null;
  },
);

// ── Lifecycle ────────────────────────────────────────────────────────────
onBeforeMount(() => {
  // Resume an existing PI rather than creating a new one. The backend
  // returns pay_payment_intent on the active transaction only when the
  // pay ledger is unresolved, so when this is set we know the customer
  // has work to do against an existing PI — render the wrapper around it.
  const resumed = premiumStore.activeOrderTransaction?.pay_payment_intent;
  if (resumed && !paymentIntent.value) {
    paymentIntent.value = resumed as Record<string, unknown>;
  }
});

onMounted(async () => {
  if (premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.REFUND_PENDING) {
    if (!activeOrderId.value) return;
    const result = await pollRefundBatchConfirmation({ orderId: activeOrderId.value });
    if (result === "success") {
      // Refund batch settled — refetch the transaction so the unified
      // state (and any downstream views) sees the post-batch state.
      await premiumStore.fetchActiveOrderTransaction(activeOrderId.value);
    }
    return;
  }

  if (
    checkoutStep.value === CHECKOUT_STEPS.IDLE &&
    premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.PENDING
  ) {
    await quietlyResolvePendingPayment();
  }
});

onBeforeUnmount(() => {
  checkoutCancelled.value = true;
  if (editRedirectTimer.value) {
    clearTimeout(editRedirectTimer.value);
    editRedirectTimer.value = null;
  }
  cancelRefundBatchPolling();
  if (cartErrorClearTimer) clearTimeout(cartErrorClearTimer);
});

// Success view (checkoutStep COMPLETE *or* resumed pay-ledger COMPLETE) must
// never leave the unsaved-changes guard armed — otherwise "Orders" / the
// post-submit redirect trips the confirm dialog.
watch(checkoutView, (view) => {
  if (view === "success") {
    ordersStore.setHasUnsavedChanges(false);
  }
}, { immediate: true });

// ── Actions ──────────────────────────────────────────────────────────────

async function startCheckout() {
  // Hard stop once the order is locked (past cut-off): never create a
  // PaymentIntent the order can no longer accept. The page overlay is the
  // visual signal; this is the enforcement. Backend is the final guard.
  if (props.disableInputs) return;
  // Menu-customisation gate: never start checkout while a line is unavailable. The
  // CartSummary button is already disabled on this flag; this is the belt-and-braces
  // guard (the backend assertOrderable is the authoritative one).
  //
  // Read from the store as well as the prop: the prop is absent wherever the cart is
  // mounted by a layout rather than a page, and a guard that only half-applies is not a
  // guard. CartSummary derives its banner and button the same way.
  //
  // Scoped on `isGroupOrder`, NOT `isPremium` — the same scope the store page's
  // `orderabilityBlocked` uses. Only Group Select has a frozen menu to drift from, and the
  // failures array cannot say which order it described, so a Set order sharing the premium
  // cart type would otherwise inherit a group order's verdict and become impossible to
  // check out with no control anywhere to clear it.
  if (props.orderabilityBlocked) return;
  if (isGroupOrder.value && premiumStore.orderabilityFailures.length > 0) return;
  ordersStore.setCheckoutError(false);
  ordersStore.setCartError(false);
  terminalError.value = null;
  checkoutInfo.value = { title: "", body: "", action: { text: "", handler: () => {} } };
  checkoutStep.value = CHECKOUT_STEPS.CREATING_INTENT;

  try {
    const checkoutRes = await initCheckout();
    checkoutType.value = checkoutRes?.requires_payment ? "payment" : "order";

    // Sync activeOrderTransaction so userOrderPaymentStatus goes non-FRESH
    // and product operators disable while payment is in progress.
    // Fire-and-forget — payment flow proceeds regardless of fetch outcome.
    if (activeOrderId.value && checkoutRes?.requires_payment) {
      premiumStore.fetchActiveOrderTransaction(activeOrderId.value).catch(() => {});
    }

    track("premium_checkout_started", {
      order_id: premiumStore.selectedPremiumOrderConfig?.orderId || "",
      requires_payment: !!checkoutRes?.requires_payment,
			email: authStore.user?.email,
			organisation: authStore.organisation?.name,
    });

    if (await handleRefundCheckoutResponse(checkoutRes)) return;

    if (!checkoutRes?.requires_payment) {
      completeCheckout();
      return;
    }

    paymentIntent.value = checkoutRes.payment_intent as Record<string, unknown>;
    ordersStore.setStripePaymentIntentStatus(null);
    checkoutStep.value = CHECKOUT_STEPS.PAYING;
  } catch {
    // Show the error view for a beat, then fall back to the editable cart.
    // Using an explicit ERROR step (not IDLE) guarantees the error view renders
    // even if the throw didn't populate cartError/checkoutError — checkoutView's
    // early ERROR branch wins over the "processing" spinner, and checkoutErrorCopy
    // provides a generic fallback message.
    checkoutStep.value = CHECKOUT_STEPS.ERROR;
    scheduleCartErrorClear(() => {
      if (checkoutStep.value === CHECKOUT_STEPS.ERROR) {
        checkoutStep.value = CHECKOUT_STEPS.IDLE;
      }
    });
  }
}

/**
 * Group orders retry through initPremiumCheckout (a fresh checkout call)
 * rather than the public retry endpoint — so the StripePaymentWrapper's
 * internal retry is disabled via :use-internal-retry="!isGroupOrder" and
 * it routes the retry click through to this method instead.
 */
function retryFailedPayment() {
  if (props.disableInputs) return;
  if (isGroupOrder.value) return startCheckout();
}

function onInternalRetryStart() {
  ordersStore.setCheckoutError(false);
  ordersStore.setCartError(false);
  terminalError.value = null;
  checkoutType.value = "payment";
}

function completeCheckout({checkoutTypeOveride}: {checkoutTypeOveride?: string} = {}) {
  checkoutStep.value = CHECKOUT_STEPS.COMPLETE;
  ordersStore.setStripePaymentIntentStatus(null);
  ordersStore.setCartLoading(false);
  ordersStore.setCheckoutError(false);
  ordersStore.setCartError(false);
  ordersStore.setChosenOrder?.(false);
  // Always clear here — not only via the page inject. On mobile the
  // authenticated layout can mount its own ShoppingCart outside the page
  // provide() tree, so premiumCheckoutComplete is null and the dirty flag
  // (plus the post-success redirect) would otherwise never reset. Jordan:
  // "the unsaved changes tracking isn't resetting".
  ordersStore.setHasUnsavedChanges(false);

  track("premium_payment_completed", {
    order_id: premiumStore.selectedPremiumOrderConfig?.orderId || "",
    email: authStore.user?.email,
    organisation: authStore.organisation?.name,
  });
  if (isEditMode.value) {
    const { $notify } = useNuxtApp();
    $notify?.({
      text: `Your ${checkoutTypeOveride || checkoutType.value} has been successfully processed`,
      variant: "success",
    });
  }
  if (isEditMode.value && premiumCheckoutComplete) {
    premiumCheckoutComplete();
  } else if (isEditMode.value) {
    // Inject missing (layout-mounted cart): still leave the success screen
    // for the page after a short beat, matching handleCheckoutComplete.
    if (editRedirectTimer.value) clearTimeout(editRedirectTimer.value);
    editRedirectTimer.value = setTimeout(() => {
      if (checkoutCancelled.value) return;
      navigateTo("/orders/upcoming", { replace: true });
    }, 1500);
  }
}

/**
 * Returns true when the response was a refund flow (terminal or polled
 * to a resolution) and the caller should stop the checkout dispatch.
 * Returns false when the response carries no refund involvement and the
 * caller should proceed to the payment branch.
 */
async function handleRefundCheckoutResponse(
  checkoutRes: Record<string, unknown> | null | undefined,
): Promise<boolean> {
  if (!checkoutRes) return false;

  if (checkoutRes.status === "partial_failure") {
    terminalError.value = { kind: "partial_failure" };
    return true;
  }
  if (checkoutRes.status === "failure") {
    terminalError.value = { kind: "batch_failed" };
    return true;
  }
  if (!checkoutRes.requires_refund) return false;

  if (!activeOrderId.value) return false;

  checkoutStep.value = CHECKOUT_STEPS.CONFIRMING;
  const result = await pollRefundBatchConfirmation({ orderId: activeOrderId.value });

  if (result === "cancelled") return true;
  if (result === "success") {
    completeCheckout({checkoutTypeOveride: "refund"});
    return true;
  }
  if (result === "failed") {
    terminalError.value = { kind: "batch_failed" };
    return true;
  }
  // timeout — refund still processing. Degrade to the passive
  // refund_pending view rather than locking on the spinner.
  checkoutStep.value = CHECKOUT_STEPS.INCOMPLETE_REFUND;
  return true;
}

/** Group flow → initPremiumCheckout. Single-edit flow → modifyOrder. */
async function initCheckout(): Promise<Record<string, unknown>> {
  const cart = ordersStore.cart;
  if (!cart || !(cart as Cart).items) return {};

  // CartItemInput accepts the raw cart-item shape (product / quantity /
  // appliedModifiers / order_product_id) — no remapping needed.
  const items = prepareItemsForUpdate((cart as Cart).items as never);

  if (isGroupOrder.value) {
    const orderId = premiumStore.selectedPremiumOrderConfig?.orderId;
    if (!orderId) return {};
    return premiumStore.initPremiumCheckout({
      items,
      orderId,
    });
  }

  if (!activeOrderId.value) return {};
  return ordersStore.modifyOrder({
    orderId: activeOrderId.value as string | number,
    products: items as never,
    deliveryDateTime: formatDeliveryDateTime() ?? undefined,
  });
}

function formatDeliveryDateTime(): string | null {
  const cart = ordersStore.cart as Cart | false;
  if (!cart || !(cart as Cart).date) return null;
  const date = (cart as Cart).date as Record<string, unknown> | false;
  if (!date) return null;
  const full = date.full as string | undefined;
  const window = date.selectedWindow as string | undefined;
  if (!full || !window) return null;
  return `${full} ${window}`;
}

async function invalidateFailedCheckout() {
  try {
    const paymentIntentId =
      (premiumStore.activeOrderTransaction?.pay_ledger as { payment_intent_id?: string } | null)
        ?.payment_intent_id ?? (paymentIntent.value as { id?: string } | null)?.id;
    if (!paymentIntentId) return;
    await premiumStore.invalidatePayLedger(paymentIntentId);
    checkoutStep.value = CHECKOUT_STEPS.IDLE;
    paymentIntent.value = null;
  } catch {
    scheduleCartErrorClear();
  }
}

async function onPaymentIntentCancel() {
  try {
    const paymentIntentId =
      (premiumStore.activeOrderTransaction?.pay_ledger as { payment_intent_id?: string } | null)
        ?.payment_intent_id ?? (paymentIntent.value as { id?: string } | null)?.id;
    if (!paymentIntentId) return;
    await premiumStore.cancelPayment(paymentIntentId);
    checkoutStep.value = CHECKOUT_STEPS.IDLE;
    ordersStore.setCheckoutError(false);
    ordersStore.setCartError(false);
  } catch {
    scheduleCartErrorClear(() => {
      checkoutStep.value = CHECKOUT_STEPS.PAYING;
    });
  }
}

function goToOrders() {
  // Success / terminal / pending views are post-submit — never block with
  // the unsaved-changes confirm. Clear first so useUnsavedChanges lets us go.
  ordersStore.setHasUnsavedChanges(false);
  navigateTo("/orders/upcoming", { replace: true });
}

/**
 * Resume polling on cold mount when the customer's active transaction
 * shows PENDING. We don't render the StripePayment wrapper for this — the
 * "finalising" copy is shown instead — but we still need to drive the
 * UI forward when the webhook lands. This polls the public ledger-status
 * endpoint quietly and refetches the transaction once the ledger settles.
 */
async function quietlyResolvePendingPayment() {
  const paymentIntentId = premiumStore.activeOrderTransaction?.pay_ledger?.payment_intent_id;
  if (!paymentIntentId || !activeOrderId.value) return;

  const result = await pollPayLedgerStatus({ paymentIntentId });
  if (checkoutCancelled.value) return;

  // succeeded / failed / cancelled all resolve here — fall through to
  // refetch so the active-transaction view (and userOrderPaymentStatus)
  // mirrors the new state.
  if (result === "succeeded" || result === "failed") {
    await premiumStore.fetchActiveOrderTransaction(activeOrderId.value);
  }
}
</script>

<template>
  <div class="shopping-cart">
    <div v-if="checkoutView === 'processing'" class="status-container">
      <fa :icon="['fal', 'spinner']" class="fa-spin status-icon-sm" />
      <p>
        <strong>Processing your {{ checkoutType }}</strong>
      </p>
      <p>This can take a few seconds. Please don't close this window.</p>
    </div>

    <div v-else-if="checkoutView === 'error'" class="status-container">
      <fa :icon="['fal', 'circle-exclamation']" class="status-icon-error" />
      <p class="error">
        {{ checkoutErrorCopy }}
      </p>
    </div>

    <div v-else-if="checkoutView === 'refund_pending'" class="status-container">
      <fa :icon="['fal', 'circle-info']" class="status-icon-error" />
      <p><strong>Your refund is still being confirmed</strong></p>
      <p>Your changes have been submitted. You can track the refund on your upcoming orders.</p>
      <DynamicButton
        class="info-action-button"
        color="pink"
        text="Orders"
        @click-event="goToOrders"
      />
    </div>

    <div v-else-if="checkoutView === 'finalising'" class="status-container">
      <fa :icon="['fal', 'circle-info']" class="status-icon-error" />
      <p><strong>We're still finalising a previous payment on this order</strong></p>
      <p>
        This page will update as soon as it clears. If it doesn't resolve shortly please contact
        support.
      </p>
      <DynamicButton
        class="info-action-button"
        color="pink"
        text="Orders"
        @click-event="goToOrders"
      />
    </div>

    <div v-else-if="checkoutView === 'terminal'" class="status-container">
      <fa :icon="['fal', 'circle-exclamation']" class="status-icon-error" />
      <p>
        <strong>{{ terminalCopy.title }}</strong>
      </p>
      <p>{{ terminalCopy.body }}</p>
      <DynamicButton
        class="info-action-button"
        color="pink"
        text="Back to orders"
        @click-event="goToOrders"
      />
    </div>

    <div v-else-if="checkoutView === 'success'" class="status-container">
      <fa :icon="['fad', 'square-check']" class="status-icon-success" />
      <p>
        <strong>Your {{ checkoutType }} has been successfully processed</strong>
      </p>
      <p>Please proceed to Upcoming Orders to view your orders.</p>
      <DynamicButton
        class="info-action-button"
        color="pink"
        text="Orders"
        @click-event="goToOrders"
      />
    </div>

    <div v-else-if="checkoutView === 'info'" class="status-container">
      <fa :icon="['fal', 'circle-info']" class="status-icon-error" />
      <p>
        <strong>{{ checkoutInfo.title }}</strong>
      </p>
      <p>{{ checkoutInfo.body }}</p>
      <DynamicButton
        v-if="checkoutInfo.action.text"
        class="info-action-button"
        color="pink"
        :text="checkoutInfo.action.text"
        @click-event="checkoutInfo.action.handler"
      />
    </div>

    <StripePaymentWrapper
      v-else-if="canRenderStripe && activeOrderId"
      :order-id="activeOrderId"
      :payment-intent="paymentIntent as never"
      :initial-view="initialPaymentView"
      :is-premium-group-order="isPremium"
      :book-delivery="false"
      :allow-start-again="true"
      :show-cancel="true"
      :use-internal-retry="!isGroupOrder"
      @confirmed="completeCheckout"
      @cancel="onPaymentIntentCancel"
      @retry="retryFailedPayment"
      @retry:start="onInternalRetryStart"
      @start-again="invalidateFailedCheckout"
    />

    <CartSummary v-else :disable-inputs="disableInputs" :orderability-blocked="orderabilityBlocked" @checkout:start="startCheckout" />
  </div>
</template>

<style lang="scss" scoped>
$laptop: 1200px;
$tablet: 768px;
$mobile: 600px;

.shopping-cart {
  right: 0;
  z-index: $z-cart-fixed;
  padding: 1rem;
  background-color: var(--color-white-1);
  border: 2px solid var(--color-grey-3);
  max-height: 65vh;
  overflow: hidden;
  border-radius: 0.4rem;
  @media (max-width: $tablet) {
    border-radius: 0;
    box-sizing: border-box;
    // Teleported to <body> on mobile (see store/[slug].vue,
    // premium/store/[slug].vue, orders/[id]/edit.vue) — this component
    // owns the overlay's full positioning/sizing itself so it isn't split
    // across the page-level wrapper and this scoped style (that split is
    // what caused the 2026-08-23 hotfix's specificity fights). `top`
    // reuses the hotfix's validated nav-height offset. Width comes from
    // left+right (not an explicit width, which would be redundant with
    // `right: 0` above and rely on the browser's over-constrained-box
    // tie-break rather than being explicit).
    position: fixed;
    top: 5rem;
    left: 0;
    height: calc(100dvh - 5rem);
    max-height: none;
    border: none;
    z-index: $z-cart-overlay;
  }

  & .status-container {
    text-align: center;
    padding: 5rem 0;

    & .info-action-button {
      margin-top: 1rem;
    }
  }
}

.status-icon-sm {
  height: 2rem;
  width: 2rem;
}

.status-icon-error {
  color: #ff1e5f;
  height: 2rem;
  width: 2rem;
}

.status-icon-success {
  --fa-primary-color: #02b37a;
  --fa-secondary-color: #02b37a0d;
  height: 2rem;
  width: 2rem;
}
</style>
